/**
 * Cache de front de las go scenes en IndexedDB — PRIMERA (y única) pieza de
 * IndexedDB del móvil. El origen de verdad es la DB del backend (`go_user_scenes`);
 * esta cache existe solo para PINTAR AL INSTANTE al arrancar (sin esperar la red):
 * hidratamos el atom desde acá y en paralelo un `GET /go/scenes` reconcilia.
 *
 * Deliberadamente mínima y aislada a este wrapper (sin Dexie/idb, cero deps):
 * DB `eriol-go`, store `goScenes`, clave fija `"current"`. Todo fail-soft — si
 * IndexedDB no está disponible (modo privado / navegador viejo) devolvemos null
 * o no-op sin tirar, así el arranque nunca se rompe por la cache.
 */

import type { SceneTemplate } from "@/lib/scene.types";

const DB_NAME = "eriol-go";

const STORE = "goScenes";

const KEY = "current";

// El catálogo de escenas (definiciones) se cachea en el MISMO store con otra

// clave — así no hace falta bump de versión de IndexedDB.

const CATALOG_KEY = "catalog";

export interface GoScenesCacheValue {
  chosenDesignKey: string | null;

  installedSlugs: string[];
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(null);

      return;
    }

    let req: IDBOpenDBRequest;

    try {
      req = indexedDB.open(DB_NAME, 1);
    } catch {
      // Algunos navegadores en modo privado tiran al abrir.

      resolve(null);

      return;
    }

    req.onupgradeneeded = () => {
      const db = req.result;

      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };

    req.onsuccess = () => resolve(req.result);

    req.onerror = () => resolve(null);

    // Otra pestaña bloquea el upgrade: no colgamos el arranque.

    req.onblocked = () => resolve(null);
  });
}

/** Lee el estado cacheado, o `null` si no hay / IndexedDB no está disponible. */

export async function readGoScenesCache(): Promise<GoScenesCacheValue | null> {
  const db = await openDb();

  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");

      const req = tx.objectStore(STORE).get(KEY);

      req.onsuccess = () => {
        const v = req.result as GoScenesCacheValue | undefined;

        resolve(
          v && typeof v === "object" && Array.isArray(v.installedSlugs)
            ? {
                chosenDesignKey: v.chosenDesignKey ?? null,

                installedSlugs: v.installedSlugs,
              }
            : null,
        );

        db.close();
      };

      req.onerror = () => {
        resolve(null);

        db.close();
      };
    } catch {
      resolve(null);

      db.close();
    }
  });
}

/** Escribe el estado en la cache. No-op silencioso ante cualquier fallo. */

export async function writeGoScenesCache(
  value: GoScenesCacheValue,
): Promise<void> {
  const db = await openDb();

  if (!db) return;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");

      tx.objectStore(STORE).put(
        {
          chosenDesignKey: value.chosenDesignKey ?? null,

          installedSlugs: value.installedSlugs,
        },

        KEY,
      );

      tx.oncomplete = () => {
        resolve();

        db.close();
      };

      tx.onerror = () => {
        resolve();

        db.close();
      };

      tx.onabort = () => {
        resolve();

        db.close();
      };
    } catch {
      resolve();

      db.close();
    }
  });
}

/** Lee el catálogo cacheado (definiciones de escenas), o `null`. Fail-soft. */

export async function readCatalogCache(): Promise<SceneTemplate[] | null> {
  const db = await openDb();

  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");

      const req = tx.objectStore(STORE).get(CATALOG_KEY);

      req.onsuccess = () => {
        const v = req.result as SceneTemplate[] | undefined;

        resolve(Array.isArray(v) && v.length > 0 ? v : null);

        db.close();
      };

      req.onerror = () => {
        resolve(null);

        db.close();
      };
    } catch {
      resolve(null);

      db.close();
    }
  });
}

/** Escribe el catálogo en la cache. No-op silencioso ante cualquier fallo. */

export async function writeCatalogCache(
  catalog: SceneTemplate[],
): Promise<void> {
  const db = await openDb();

  if (!db) return;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");

      tx.objectStore(STORE).put(catalog, CATALOG_KEY);

      tx.oncomplete = () => {
        resolve();

        db.close();
      };

      tx.onerror = () => {
        resolve();

        db.close();
      };

      tx.onabort = () => {
        resolve();

        db.close();
      };
    } catch {
      resolve();

      db.close();
    }
  });
}
